import React, { useRef, useState } from "react";
import { Button } from "antd";
import { InboxOutlined, FolderOpenOutlined } from "@ant-design/icons";

const IGNORED_FOLDER_FILE_NAMES = new Set([
  ".DS_Store",
  "Thumbs.db",
  "desktop.ini",
]);

const withRelativePath = (file, fullPath) => {
  file.relativePath = fullPath
    .split("/")
    .filter(Boolean)
    .slice(0, -1)
    .join("/");
  return file;
};

const readAllDirectoryEntries = (reader) =>
  new Promise((resolve, reject) => {
    const entries = [];
    const readBatch = () => {
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(entries);
          return;
        }
        entries.push(...batch);
        readBatch();
      }, reject);
    };
    readBatch();
  });

const collectEntryFiles = async (entry) => {
  if (entry.isFile) {
    if (IGNORED_FOLDER_FILE_NAMES.has(entry.name)) return [];
    const file = await new Promise((resolve, reject) =>
      entry.file(resolve, reject),
    );
    return [withRelativePath(file, entry.fullPath)];
  }
  const children = await readAllDirectoryEntries(entry.createReader());
  const nestedFiles = await Promise.all(children.map(collectEntryFiles));
  return nestedFiles.flat();
};

const FileUpload = ({
  onUpload,
  accept = "*/*",
  maxSize = 262144000, // 250MB default
  multiple = false,
  allowFolders = false,
  className = "",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const folderInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const validateFile = (file) => {
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();

    if (file.size > maxSize) {
      setError(`File size should not exceed ${maxSize / 1024 / 1024}MB`);
      return false;
    } else if (file.size == 0) {
      setError(`File size should be more than 0 bytes`);
      return false;
    }

    if (accept !== "*/*") {
      const acceptedTypes = accept
        .split(",")
        .map((type) => type.trim().toLowerCase());

      const isAcceptedByType = acceptedTypes.some((type) => {
        if (type.startsWith(".")) {
          return type === fileExtension;
        } else {
          const regex = new RegExp(type.replace("*", ".*"));
          return regex.test(file.type);
        }
      });

      if (!isAcceptedByType) {
        setError(`File type not accepted. Please upload ${accept}`);
        return false;
      }
    }

    return true;
  };

  const handleFiles = (files) => {
    setError("");
    const validFiles = Array.from(files).filter(validateFile);
    if (validFiles.length > 0) {
      onUpload(multiple ? validFiles : validFiles[0]);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const entries = allowFolders
      ? Array.from(e.dataTransfer.items || [])
          .map((item) => item.webkitGetAsEntry?.())
          .filter(Boolean)
      : [];
    if (entries.length === 0) {
      handleFiles(e.dataTransfer.files);
      return;
    }
    Promise.all(entries.map(collectEntryFiles))
      .then((files) => handleFiles(files.flat()))
      .catch(() => setError("Could not read the dropped folder"));
  };

  const handleChange = (e) => {
    handleFiles(e.target.files);
    e.target.value = "";
  };

  const handleFolderChange = (e) => {
    const files = Array.from(e.target.files)
      .filter((file) => !IGNORED_FOLDER_FILE_NAMES.has(file.name))
      .map((file) => withRelativePath(file, file.webkitRelativePath));
    handleFiles(files);
    e.target.value = "";
  };

  return (
    <div className={`w-full ${className}`}>
      <div
        className={`relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          ${
            isDragging
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:border-gray-400"
          }
          transition-colors duration-200 ease-in-out`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onChange={handleChange}
          accept={accept}
          multiple={multiple}
        />
        <InboxOutlined className="text-5xl text-gray-400" />
        <p className="mt-4 text-sm text-gray-600">
          {allowFolders
            ? "Drag and drop files or a folder here, or click to select files"
            : "Drag and drop your file here, or click to select"}
        </p>
        {allowFolders && (
          <div className="relative z-10 mt-3">
            <input
              ref={folderInputRef}
              type="file"
              className="hidden"
              onChange={handleFolderChange}
              webkitdirectory=""
              multiple
            />
            <Button
              icon={<FolderOpenOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                folderInputRef.current?.click();
              }}
            >
              Select Folder
            </Button>
          </div>
        )}
        <p className="mt-2 text-xs text-gray-500">
          {accept !== "*/*"
            ? `Accepted files: ${accept}`
            : "All file types accepted"}
        </p>
        {maxSize && (
          <p className="text-xs text-gray-500">
            Max size: {maxSize / 1024 / 1024}MB
          </p>
        )}
      </div>
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default FileUpload;
